import React, { useMemo, useState, useEffect, useRef } from "react";
import { db } from '../../services/db';
import { Student, ExamRecord, Subject, SchoolData, User, GradingScheme } from '../../types';
import QRCode from 'qrcode';
import { Printer, Download, FileText, Loader2, ShieldCheck, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { cn } from "../../utils/cn";

const defaultScheme: GradingScheme[] = [
  { id: 'g1', school_id: 'all', min_score: 80, max_score: 100, grade_label: 'E', color_code: '#22c55e', remarks: 'Excellent' },
  { id: 'g2', school_id: 'all', min_score: 60, max_score: 79, grade_label: 'VG', color_code: '#1e5baf', remarks: 'Very Good' },
  { id: 'g3', school_id: 'all', min_score: 50, max_score: 59, grade_label: 'G', color_code: '#fbbf24', remarks: 'Good' },
  { id: 'g4', school_id: 'all', min_score: 0, max_score: 49, grade_label: 'F', color_code: '#ef4444', remarks: 'Fair' }
];

function getRankSuffix(num: number): string {
  const j = num % 10, k = num % 100;
  if (j === 1 && k !== 11) {
    return num + "st";
  }
  if (j === 2 && k !== 12) {
    return num + "nd";
  }
  if (j === 3 && k !== 13) {
    return num + "rd";
  }
  return num + "th";
}

export function getStudentAverageForTerm(
  studentId: string,
  termName: string,
  studentRecs: ExamRecord[],
  studentClassGrade: string | undefined,
  schoolSubjects: Subject[]
) {
  const isNurseryStudent = studentClassGrade?.toLowerCase().includes('nursery') || 
                           studentClassGrade?.toLowerCase().includes('baby') || 
                           studentClassGrade?.toLowerCase().includes('middle') || 
                           studentClassGrade?.toLowerCase().includes('top');
  
  const NURSERY_SUBJECTS = [
    "Ikinyarwanda", "English", "Imibare", "Ubugeni n'Umuco", 
    "Imyitwarire n'iterambere mu mbamutima", "Ibonezabuzima", "Ubumenyi bw'Ibidukikije"
  ];

  const displaySubjects = isNurseryStudent
    ? NURSERY_SUBJECTS.map((name, idx) => {
        const existing = schoolSubjects.find(s => s.name === name);
        return existing || { id: `nursery_${idx}`, name, max_marks: 100, school_id: 'all' } as Subject;
      })
    : schoolSubjects;

  const hasAnyScore = studentRecs.some(r => r.term === termName && (
      (r.score !== undefined && r.score !== null && r.score !== '') ||
      (r.test_score !== undefined && r.test_score !== null && r.test_score !== '') ||
      (r.exam_score !== undefined && r.exam_score !== null && r.exam_score !== '')
  ));

  if (!hasAnyScore) return -1;

  const scores = displaySubjects.map(sub => {
    const record = studentRecs.find(r => 
      r.term === termName && (
        (r.subject_id === sub.id) || 
        (r.subject_name && sub.name && r.subject_name.toLowerCase().trim() === sub.name.toLowerCase().trim())
      )
    );
    if (!record) return null;

    const testVal = record.test_score !== undefined && record.test_score !== null && record.test_score !== '' ? Number(record.test_score) : null;
    const examVal = record.exam_score !== undefined && record.exam_score !== null && record.exam_score !== '' ? Number(record.exam_score) : null;
    const scoreVal = record.score !== undefined && record.score !== null && record.score !== '' ? Number(record.score) : null;

    if (testVal !== null || examVal !== null) {
      // Test is out of 100, Exam is out of 100, so sum is out of 200. We scale to 100.
      return ((testVal || 0) + (examVal || 0)) / 2;
    }
    if (scoreVal !== null) {
      return scoreVal;
    }
    return null;
  }).filter(s => s !== null) as number[];

  if (scores.length === 0) return -1;

  const total = scores.reduce((a, b) => a + b, 0);
  return total / scores.length;
}

interface ReportCardProps {
  student: Student;
  className?: string;
  records: ExamRecord[];
  subjects: Subject[];
  term: string;
  school: SchoolData | null;
  headTeacher: User | null;
  rank?: { position: number, total: number };
  allTermRankings?: Record<string, { position: number, total: number }>;
  subjectRankings?: Record<string, { position: number, total: number }>;
  config: {
    nextTerm: string;
    note: string;
    showRank: boolean;
    showAttendance: boolean;
    showConduct: boolean;
  };
  gradingScheme?: GradingScheme[];
}

export const SingleReportCard: React.FC<ReportCardProps & { reportRef?: (el: HTMLDivElement | null) => void, academicYearName?: string }> = ({ student, className, records, subjects, term, school, headTeacher, rank, allTermRankings, subjectRankings, config, reportRef, academicYearName, gradingScheme = [] }) => {
  
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  useEffect(() => {
    const generateQR = async () => {
      if (!student.id && !student.index_number) return;
      
      const baseUrl = window.location.origin;
      const studentId = student.id || student.index_number;
      
      // Basic security hash to prevent easy manipulation
      const salt = "SSF_SECURE_2026";
      let hash = 0;
      const str = studentId + salt;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      const h = Math.abs(hash).toString(36);
      
      const verifyUrl = `${baseUrl}/result?id=${studentId}&h=${h}&t=${encodeURIComponent(term)}${academicYearName ? `&ay=${encodeURIComponent(academicYearName)}` : ''}`;
      
      try {
        const url = await QRCode.toDataURL(verifyUrl, {
          width: 140,
          margin: 1,
          color: {
            dark: '#1e3a8a', // Highly professional academic deep navy blue
            light: '#ffffff'
          }
        });
        setQrCodeUrl(url);
      } catch (err) {
        console.error('Error generating QR code', err);
      }
    };
    generateQR();
  }, [student.id, student.index_number, school?.sdms_code, term, academicYearName]);

  const NURSERY_SUBJECTS = [
    "Ikinyarwanda", "English", "Imibare", "Ubugeni n'Umuco", 
    "Imyitwarire n'iterambere mu mbamutima", "Ibonezabuzima", "Ubumenyi bw'Ibidukikije"
  ];

  const isNursery = student.class_grade?.toLowerCase().includes('nursery') || 
                   student.class_grade?.toLowerCase().includes('baby') || 
                   student.class_grade?.toLowerCase().includes('middle') || 
                   student.class_grade?.toLowerCase().includes('top');

  const displaySubjects = useMemo(() => {
    if (isNursery) {
      return NURSERY_SUBJECTS.map((name, idx) => {
          const existing = subjects.find(s => s.name === name);
          return existing || { id: `nursery_${idx}`, name, max_marks: 100, school_id: 'all' } as Subject;
      });
    }
    return subjects;
  }, [subjects, isNursery]);

  const subjectData = useMemo(() => {
    return displaySubjects.map(sub => {
      const getRecord = (t: string) => records.find(r => 
        r.term === t && (
          (r.subject_id === sub.id) || 
          (r.subject_name && sub.name && r.subject_name.toLowerCase().trim() === sub.name.toLowerCase().trim())
        )
      );
      const t1 = getRecord('Term 1');
      const t2 = getRecord('Term 2');
      const t3 = getRecord('Term 3');

      const parseScore = (val: any) => (val !== undefined && val !== null && val !== '') ? Number(val) : null;

      const getSubjectTermAverage = (test: number | null, exam: number | null, total: number | null) => {
        if (test !== null || exam !== null) {
          return ((test || 0) + (exam || 0)) / 2;
        }
        return total;
      };

      const s1 = getSubjectTermAverage(parseScore(t1?.test_score), parseScore(t1?.exam_score), parseScore(t1?.score));
      const s2 = getSubjectTermAverage(parseScore(t2?.test_score), parseScore(t2?.exam_score), parseScore(t2?.score));
      const s3 = getSubjectTermAverage(parseScore(t3?.test_score), parseScore(t3?.exam_score), parseScore(t3?.score));

      let sum = 0, count = 0;
      if (s1 !== null) { sum += s1; count++; }
      if (s2 !== null) { sum += s2; count++; }
      if (s3 !== null) { sum += s3; count++; }

      const annualAvg = count > 0 ? sum / count : null;

      return {
        subject: sub,
        t1Test: parseScore(t1?.test_score),
        t1Exam: parseScore(t1?.exam_score),
        t1Total: parseScore(t1?.score),
        t2Test: parseScore(t2?.test_score),
        t2Exam: parseScore(t2?.exam_score),
        t2Total: parseScore(t2?.score),
        t3Test: parseScore(t3?.test_score),
        t3Exam: parseScore(t3?.exam_score),
        t3Total: parseScore(t3?.score),
        annualAvg
      };
    });
  }, [displaySubjects, records]);

  const getTermAverage = (t: 't1' | 't2' | 't3') => {
    const termName = t === 't1' ? 'Term 1' : t === 't2' ? 'Term 2' : 'Term 3';
    const avg = getStudentAverageForTerm(student.id, termName, records, student.class_grade, subjects);
    return avg >= 0 ? avg : 0;
  };

  const getFormattedRank = (termName: string) => {
    const r = allTermRankings?.[termName];
    if (!r || !r.position) return "-";
    return `${getRankSuffix(r.position)} / ${r.total}`;
  };

  const getTermTotal = (t: 't1' | 't2' | 't3') => {
    const termName = t === 't1' ? 'Term 1' : t === 't2' ? 'Term 2' : 'Term 3';
    const isNurseryStudent = student.class_grade?.toLowerCase().includes('nursery') || 
                             student.class_grade?.toLowerCase().includes('baby') || 
                             student.class_grade?.toLowerCase().includes('middle') || 
                             student.class_grade?.toLowerCase().includes('top');
    
    const displaySubs = isNurseryStudent
      ? NURSERY_SUBJECTS.map((name, idx) => {
          const existing = subjects.find(s => s.name === name);
          return existing || { id: `nursery_${idx}`, name, max_marks: 100, school_id: 'all' } as Subject;
        })
      : subjects;

    const scores = displaySubs.map(sub => {
      const record = records.find(r => 
        r.term === termName && (
          (r.subject_id === sub.id) || 
          (r.subject_name && sub.name && r.subject_name.toLowerCase().trim() === sub.name.toLowerCase().trim())
        )
      );
      if (!record) return null;

      if (record.score !== undefined && record.score !== null && record.score !== '') {
        return Number(record.score);
      }
      const testVal = record.test_score !== undefined && record.test_score !== null && record.test_score !== '' ? Number(record.test_score) : null;
      const examVal = record.exam_score !== undefined && record.exam_score !== null && record.exam_score !== '' ? Number(record.exam_score) : null;
      if (testVal !== null || examVal !== null) {
        return (testVal || 0) + (examVal || 0);
      }
      return null;
    }).filter(s => s !== null) as number[];

    if (scores.length === 0) return 0;
    return scores.reduce((a, b) => a + b, 0);
  };

  const t1Avg = getTermAverage('t1');
  const t2Avg = getTermAverage('t2');
  const t3Avg = getTermAverage('t3');

  const annualAvg = useMemo(() => {
    let sum = 0, count = 0;
    if (t1Avg > 0) { sum += t1Avg; count++; }
    if (t2Avg > 0) { sum += t2Avg; count++; }
    if (t3Avg > 0) { sum += t3Avg; count++; }
    return count > 0 ? sum / count : 0;
  }, [t1Avg, t2Avg, t3Avg]);

  const t1Total = getTermTotal('t1');
  const t2Total = getTermTotal('t2');
  const t3Total = getTermTotal('t3');

  const maxPossiblePerTerm = displaySubjects.length * 100;

  const navy = "#0d2137";
  const blue = "#1e5baf";

  const getGradeInfo = (score: number) => {
    const activeScheme = gradingScheme && gradingScheme.length > 0 ? gradingScheme : defaultScheme;
    const matched = activeScheme.find(g => score >= g.min_score && score <= g.max_score);
    if (matched) {
      return {
        color: matched.color_code || '#fff',
        label: (matched.remarks || matched.grade_label || '').toString().toUpperCase(),
        grade: matched.grade_label || ''
      };
    }
    // Fallback if score is slightly out of bounds
    if (score > 100) {
      const maxGrade = [...activeScheme].sort((a,b) => b.max_score - a.max_score)[0];
      if (maxGrade) return { color: maxGrade.color_code, label: (maxGrade.remarks || maxGrade.grade_label || '').toString().toUpperCase(), grade: maxGrade.grade_label || '' };
    }
    const minGrade = [...activeScheme].sort((a,b) => a.min_score - b.min_score)[0];
    if (minGrade) return { color: minGrade.color_code, label: (minGrade.remarks || minGrade.grade_label || '').toString().toUpperCase(), grade: minGrade.grade_label || '' };
    return { color: "#fff", label: "", grade: "" };
  };

  const getRankText = (avg: number) => {
    if (avg <= 0) return '';
    const info = getGradeInfo(avg);
    const labelUpper = info.label.toUpperCase().replace(/\s+/g, '');
    const gradeUpper = info.grade.toUpperCase();
    
    if (labelUpper.includes('EXCELLENT') || gradeUpper === 'E') {
      return 'EXCELLENT';
    }
    if (labelUpper.includes('VERYGOOD') || gradeUpper === 'VG') {
      return 'VERY GOOD';
    }
    if (labelUpper.includes('GOOD') || gradeUpper === 'G') {
      return 'GOOD';
    }
    if (labelUpper.includes('FAIR') || gradeUpper === 'F') {
      return 'FAIR';
    }
    return '';
  };

  const getTextColorForBackground = (bgColor: string) => {
    if (!bgColor) return "#000";
    const lower = bgColor.toLowerCase();
    if (lower === '#fbbf24' || lower === '#fbfb24' || lower.includes('yellow') || lower === '#fff' || lower === '#ffffff') {
      return "#000";
    }
    return "#fff";
  };

  const renderCell = (score: number | null) => {
    if (score === null) return <td style={{ border: "1px solid #000", background: "#fff" }}></td>;
    const info = getGradeInfo(score);
    return (
      <td style={{ 
        border: "1px solid #000", 
        background: info.color || "#fff", 
        color: getTextColorForBackground(info.color || "#fff"),
        textAlign: "center",
        fontWeight: "bold",
        fontSize: "12px",
        padding: "4px"
      }}>
      </td>
    );
  };

  return (
    <div ref={reportRef} className="report-card-container" style={{
      width: "210mm",
      height: "297mm",
      background: "#fff",
      padding: "0",
      boxSizing: "border-box",
      position: "relative",
      fontFamily: "Arial, sans-serif",
      color: "#000",
      overflow: "hidden"
    }}>
      <div style={{
        border: "4px double #000",
        height: "100%",
        padding: "4px",
        boxSizing: "border-box"
      }}>
        <div style={{
          border: "1px solid #000",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: "20px 30px 25px"
        }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: 15 }}>
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/17/Coat_of_arms_of_Rwanda.svg/500px-Coat_of_arms_of_Rwanda.svg.png" alt="Rwanda" style={{ width: 80, height: 80, objectFit: "contain" }} crossOrigin="anonymous" />
              <div style={{ fontSize: 13, fontWeight: "bold", lineHeight: "1.4" }}>
                <div>REPUBLIC OF RWANDA</div>
                <div>MINISTRY OF EDUCATION</div>
                <div style={{ fontWeight: "normal", marginTop: 5 }}>DISTRICT: {school?.district || 'Gasabo'}</div>
                <div style={{ fontWeight: "normal" }}>SECTOR: {school?.sector || 'Kacyiru'}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 15, alignItems: "flex-start" }}>
              <div style={{ textAlign: "right", fontSize: 13, fontWeight: "bold", lineHeight: "1.4" }}>
                <div style={{ fontSize: 18, marginBottom: 5 }}>{school?.name?.toUpperCase() || 'KIGALI INTERNATIONAL SCHOOL'}</div>
                <div style={{ fontWeight: "normal" }}>SCHOOL CODE: {school?.sdms_code || '..........'}</div>
                <div style={{ fontWeight: "normal" }}>TEL: {headTeacher?.phone || school?.phone || '..........'}</div>
                <div style={{ fontWeight: "normal", marginTop: 15, fontSize: 11 }}>EMAIL: {headTeacher?.email || school?.admin_email || school?.email || '..........'}</div>
                <div style={{ fontWeight: "normal", marginTop: 2, fontSize: 11 }}>ACADEMIC YEAR: {academicYearName || school?.academic_year || '2024-2025'}</div>
              </div>
              {school?.logo && (
                <img src={school.logo} alt="School Logo" style={{ width: 80, height: 80, objectFit: "contain" }} crossOrigin="anonymous" />
              )}
            </div>
          </div>

          <div style={{ borderBottom: "2px solid #000", margin: "15px 0" }}></div>

          {/* Title */}
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <h2 style={{ color: "#d32f2f", fontSize: 24, fontWeight: "bold", margin: 0, letterSpacing: "1px" }}>{isNursery ? 'NURSERY PROGRESS REPORT' : 'STUDENT PROGRESS REPORT'}</h2>
          </div>

          {/* Student Info Box */}
          <div style={{ border: "1px solid #000", padding: "15px", marginBottom: 20, backgroundColor: "#f1f5f9" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "12px 40px", fontSize: 13 }}>
              <div style={{ display: "flex" }}><span style={{ fontWeight: "bold", width: 140 }}>STUDENT NAME:</span> <span>{student.full_name.toUpperCase()}</span></div>
              <div style={{ display: "flex" }}><span style={{ fontWeight: "bold", width: 140 }}>CLASS:</span> <span>{className || 'Nursery 1'}</span></div>
              <div style={{ display: "flex" }}><span style={{ fontWeight: "bold", width: 140 }}>INDEX NUMBER:</span> <span>{student.index_number || '2024003'}</span></div>
              <div style={{ display: "flex" }}><span style={{ fontWeight: "bold", width: 140 }}>ACADEMIC YEAR:</span> <span>{academicYearName || school?.academic_year || '2024-2025'}</span></div>
              <div style={{ display: "flex" }}><span style={{ fontWeight: "bold", width: 140 }}>GENDER:</span> <span>{student.gender || '-'}</span></div>
              <div style={{ display: "flex" }}><span style={{ fontWeight: "bold", width: 140 }}>TERM:</span> <span>{term.toUpperCase()}</span></div>
              <div style={{ display: "flex" }}><span style={{ fontWeight: "bold", width: 140 }}>DOB:</span> <span>{student.dob || '-'}</span></div>
            </div>
          </div>

           {/* Main Table */}
          <div style={{ flex: 1 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000" }}>
              <thead>
                <tr>
                  <th rowSpan={2} style={{ background: navy, color: "#fff", border: "1px solid #000", padding: "12px", textAlign: "left", width: "30%", fontSize: 12 }}>SUBJECT</th>
                  <th colSpan={2} style={{ background: blue, color: "#fff", border: "1px solid #000", padding: "8px", fontSize: 12 }}>1ST TERM</th>
                  {(term === 'Term 2' || term === 'Term 3' || term === 'Annual') && <th colSpan={2} style={{ background: blue, color: "#fff", border: "1px solid #000", padding: "8px", fontSize: 12 }}>2ND TERM</th>}
                  {(term === 'Term 3' || term === 'Annual') && <th colSpan={2} style={{ background: blue, color: "#fff", border: "1px solid #000", padding: "8px", fontSize: 12 }}>3RD TERM</th>}
                  {term === 'Annual' && <th colSpan={2} style={{ background: navy, color: "#fff", border: "1px solid #000", padding: "8px", fontSize: 12 }}>ANNUAL</th>}
                </tr>
                <tr>
                  <th style={{ background: "#f0f0f0", border: "1px solid #000", padding: "5px", fontSize: 11 }}>TEST</th>
                  <th style={{ background: "#f0f0f0", border: "1px solid #000", padding: "5px", fontSize: 11 }}>EXAM</th>
                  {(term === 'Term 2' || term === 'Term 3' || term === 'Annual') && (
                    <>
                      <th style={{ background: "#f0f0f0", border: "1px solid #000", padding: "5px", fontSize: 11 }}>TEST</th>
                      <th style={{ background: "#f0f0f0", border: "1px solid #000", padding: "5px", fontSize: 11 }}>EXAM</th>
                    </>
                  )}
                  {(term === 'Term 3' || term === 'Annual') && (
                    <>
                      <th style={{ background: "#f0f0f0", border: "1px solid #000", padding: "5px", fontSize: 11 }}>TEST</th>
                      <th style={{ background: "#f0f0f0", border: "1px solid #000", padding: "5px", fontSize: 11 }}>EXAM</th>
                    </>
                  )}
                  {term === 'Annual' && (
                    <>
                      <th style={{ background: "#e2e8f0", border: "1px solid #000", padding: "5px", fontSize: 11 }}>AVG</th>
                      <th style={{ background: "#e2e8f0", border: "1px solid #000", padding: "5px", fontSize: 11 }}>GRADE</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {subjectData.map((sd) => (
                  <tr key={sd.subject.id || sd.subject.name} style={{ height: 40 }}>
                    <td style={{ border: "1px solid #000", padding: "5px 12px", fontWeight: "bold", fontSize: 13 }}>{sd.subject.name}</td>
                    {renderCell(sd.t1Test)}
                    {renderCell(sd.t1Exam)}
                    {(term === 'Term 2' || term === 'Term 3' || term === 'Annual') && (
                      <>
                        {renderCell(sd.t2Test)}
                        {renderCell(sd.t2Exam)}
                      </>
                    )}
                    {(term === 'Term 3' || term === 'Annual') && (
                      <>
                        {renderCell(sd.t3Test)}
                        {renderCell(sd.t3Exam)}
                      </>
                    )}
                    {term === 'Annual' && (
                      <>
                        <td style={{ border: "1px solid #000", padding: "5px", textAlign: "center", fontWeight: "bold", fontSize: 13, backgroundColor: "#fafafa" }}>
                          {sd.annualAvg !== null ? sd.annualAvg.toFixed(1) : '-'}
                        </td>
                        <td style={{ border: "1px solid #000", padding: "5px", textAlign: "center", fontWeight: "bold", fontSize: 13, backgroundColor: "#fafafa", color: sd.annualAvg !== null ? getGradeInfo(sd.annualAvg).color : "#000" }}>
                          {sd.annualAvg !== null ? getGradeInfo(sd.annualAvg).grade : '-'}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {/* TERM AVERAGE (%) row */}
                <tr style={{ height: 40 }}>
                  <td style={{ background: navy, color: "#fff", border: "1px solid #000", padding: "5px 12px", fontWeight: "bold", fontSize: 12 }}>TERM AVERAGE (%)</td>
                  <td colSpan={2} style={{ 
                    background: t1Avg > 0 ? getGradeInfo(t1Avg).color : "#f8fafc", 
                    color: t1Avg > 0 ? getTextColorForBackground(getGradeInfo(t1Avg).color) : "#000",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: 13,
                    border: "1px solid #000", 
                  }}>
                    {t1Avg > 0 ? '' : '-'}
                  </td>
                  {(term === 'Term 2' || term === 'Term 3' || term === 'Annual') && (
                    <td colSpan={2} style={{ 
                      background: t2Avg > 0 ? getGradeInfo(t2Avg).color : "#f8fafc", 
                      color: t2Avg > 0 ? getTextColorForBackground(getGradeInfo(t2Avg).color) : "#000",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: 13,
                      border: "1px solid #000", 
                    }}>
                      {t2Avg > 0 ? '' : '-'}
                    </td>
                  )}
                  {(term === 'Term 3' || term === 'Annual') && (
                    <td colSpan={2} style={{ 
                      background: t3Avg > 0 ? getGradeInfo(t3Avg).color : "#f8fafc", 
                      color: t3Avg > 0 ? getTextColorForBackground(getGradeInfo(t3Avg).color) : "#000",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: 13,
                      border: "1px solid #000", 
                    }}>
                      {t3Avg > 0 ? '' : '-'}
                    </td>
                  )}
                  {term === 'Annual' && (
                    <td colSpan={2} style={{ 
                      background: annualAvg > 0 ? getGradeInfo(annualAvg).color : "#f8fafc", 
                      color: annualAvg > 0 ? getTextColorForBackground(getGradeInfo(annualAvg).color) : "#000",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: 13,
                      border: "1px solid #000", 
                    }}>
                      {annualAvg > 0 ? '' : '-'}
                    </td>
                  )}
                </tr>

                {/* PLACE / RANK row */}
                <tr style={{ height: 40 }}>
                  <td style={{ background: navy, color: "#fff", border: "1px solid #000", padding: "5px 12px", fontWeight: "bold", fontSize: 12 }}>PLACE / RANK</td>
                  <td colSpan={2} style={{ 
                    background: t1Avg > 0 ? getGradeInfo(t1Avg).color : "#f8fafc", 
                    color: t1Avg > 0 ? getTextColorForBackground(getGradeInfo(t1Avg).color) : "#000",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: 12,
                    letterSpacing: "0.5px",
                    border: "1px solid #000", 
                  }}>
                    {t1Avg > 0 ? getRankText(t1Avg) : ''}
                  </td>
                  {(term === 'Term 2' || term === 'Term 3' || term === 'Annual') && (
                    <td colSpan={2} style={{ 
                      background: t2Avg > 0 ? getGradeInfo(t2Avg).color : "#f8fafc", 
                      color: t2Avg > 0 ? getTextColorForBackground(getGradeInfo(t2Avg).color) : "#000",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: 12,
                      letterSpacing: "0.5px",
                      border: "1px solid #000", 
                    }}>
                      {t2Avg > 0 ? getRankText(t2Avg) : ''}
                    </td>
                  )}
                  {(term === 'Term 3' || term === 'Annual') && (
                    <td colSpan={2} style={{ 
                      background: t3Avg > 0 ? getGradeInfo(t3Avg).color : "#f8fafc", 
                      color: t3Avg > 0 ? getTextColorForBackground(getGradeInfo(t3Avg).color) : "#000",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: 12,
                      letterSpacing: "0.5px",
                      border: "1px solid #000", 
                    }}>
                      {t3Avg > 0 ? getRankText(t3Avg) : ''}
                    </td>
                  )}
                  {term === 'Annual' && (
                    <td colSpan={2} style={{ 
                      background: annualAvg > 0 ? getGradeInfo(annualAvg).color : "#f8fafc", 
                      color: annualAvg > 0 ? getTextColorForBackground(getGradeInfo(annualAvg).color) : "#000",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: 12,
                      letterSpacing: "0.5px",
                      border: "1px solid #000", 
                    }}>
                      {annualAvg > 0 ? getRankText(annualAvg) : ''}
                    </td>
                  )}
                </tr>
                {/* Teacher Signature row */}
                <tr style={{ height: 40 }}>
                  <td style={{ border: "1px solid #000", padding: "5px 12px", color: "#666", fontSize: 12 }}>Class Teacher's Signature</td>
                  <td colSpan={2} style={{ border: "1px solid #000" }}></td>
                  {(term === 'Term 2' || term === 'Term 3' || term === 'Annual') && <td colSpan={2} style={{ border: "1px solid #000" }}></td>}
                  {(term === 'Term 3' || term === 'Annual') && <td colSpan={2} style={{ border: "1px solid #000" }}></td>}
                  {term === 'Annual' && <td colSpan={2} style={{ border: "1px solid #000" }}></td>}
                </tr>
                {/* Parent Signature row */}
                <tr style={{ height: 40 }}>
                  <td style={{ border: "1px solid #000", padding: "5px 12px", color: "#666", fontSize: 12 }}>Parent's Signature</td>
                  <td colSpan={2} style={{ border: "1px solid #000" }}></td>
                  {(term === 'Term 2' || term === 'Term 3' || term === 'Annual') && <td colSpan={2} style={{ border: "1px solid #000" }}></td>}
                  {(term === 'Term 3' || term === 'Annual') && <td colSpan={2} style={{ border: "1px solid #000" }}></td>}
                  {term === 'Annual' && <td colSpan={2} style={{ border: "1px solid #000" }}></td>}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{ marginTop: "auto", paddingTop: 15, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
            <div style={{ textAlign: "center", width: 140, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              {qrCodeUrl && (
                <div style={{ 
                  background: "#ffffff", 
                  padding: "6px", 
                  borderRadius: "10px", 
                  border: "1.5px solid #cbd5e1", 
                  display: "inline-block",
                  lineHeight: 0,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }}>
                  <img src={qrCodeUrl} alt="QR Code" style={{ width: 90, height: 90, display: "block" }} />
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 2 }}>
                <span style={{ fontSize: 9, fontWeight: "bold", color: "#1e3a8a", letterSpacing: "1px" }}>SECURE VERIFY</span>
                <span style={{ fontSize: 8, color: "#64748b", fontWeight: "bold", textTransform: "uppercase" }}>Scan to validate</span>
              </div>
            </div>

            <div style={{ border: "1px solid #000", width: 250, backgroundColor: "#f1f5f9", alignSelf: "flex-end" }}>
              <div style={{ background: navy, color: "#fff", fontSize: 10, fontWeight: "bold", textAlign: "center", padding: "4px", letterSpacing: "1px" }}>GRADE SCALE</div>
              <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column", gap: 5 }}>
                {(gradingScheme && gradingScheme.length > 0 ? gradingScheme : defaultScheme)
                  .sort((a, b) => b.min_score - a.min_score)
                  .map(item => (
                    <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 14, height: 14, background: item.color_code, border: "1px solid rgba(0,0,0,0.1)" }}></div>
                      <span style={{ fontSize: 10, fontWeight: "bold", color: "#334155" }}>
                        {(item.remarks || item.grade_label)} ({item.min_score} - {item.max_score})
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            <div style={{ textAlign: "center", width: 280, position: "relative", alignSelf: "flex-end" }}>
              <div style={{ fontWeight: "bold", fontSize: 12, marginBottom: 8, color: "#1e293b" }}>Head Teacher's Signature & Stamp:</div>
              <div style={{ height: 75, display: "flex", justifyContent: "center", alignItems: "center", marginBottom: 5 }}>
                {school?.stamp && (
                  <img src={school.stamp} alt="Signature & Stamp" style={{ width: 140, height: 70, objectFit: "contain" }} crossOrigin="anonymous" />
                )}
              </div>
              <div style={{ fontWeight: "bold", fontSize: 14, borderTop: "1.5px solid #000", paddingTop: "8px", marginTop: "5px", position: "relative", zIndex: 3, color: "#000" }}>
                {headTeacher?.full_name?.toUpperCase() || 'HEAD TEACHER'}
              </div>
              <div style={{ fontSize: 11, fontStyle: "italic", marginTop: 10, color: "#000" }}>
                Done at {school?.name || '....................'} on {new Date().toLocaleDateString('en-GB')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ReportCard: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [recordsMap, setRecordsMap] = useState<Record<string, ExamRecord[]>>({});
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [selectedTerm, setSelectedTerm] = useState('Term 3');
  const [school, setSchool] = useState<SchoolData | null>(null);
  const [headTeacher, setHeadTeacher] = useState<User | null>(null);
  const [rankings, setRankings] = useState<Record<string, Record<string, { position: number, total: number }>>>({});
  const [subjectRankings, setSubjectRankings] = useState<Record<string, Record<string, { position: number, total: number }>>>({});
  const [academicYearName, setAcademicYearName] = useState<string>('');
  const [gradingScheme, setGradingScheme] = useState<GradingScheme[]>([]);
  const [config, setConfig] = useState({
    nextTerm: '',
    note: '',
    showRank: true,
    showAttendance: true,
    showConduct: true
  });

  const reportRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const init = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sId = urlParams.get('student_id');
      const sIds = urlParams.get('student_ids');
      const classGrade = urlParams.get('class_grade');
      const stream = urlParams.get('stream');
      const term = urlParams.get('term') || 'Term 3';
      const academicYearId = urlParams.get('academic_year_id');
      const print = urlParams.get('print') === 'true';
      const urlSchoolId = urlParams.get('school_id');
      const urlHeadTeacherName = urlParams.get('head_teacher_name');
      
      setConfig({
        nextTerm: urlParams.get('next_term') || '05/01/2026',
        note: urlParams.get('note') || 'Promoted to next class',
        showRank: urlParams.get('show_rank') !== 'false',
        showAttendance: urlParams.get('show_attendance') !== 'false',
        showConduct: urlParams.get('show_conduct') !== 'false'
      });

      setSelectedTerm(term);

      const user = await db.getCurrentUser();
      const schoolId = urlSchoolId || user?.school_id || 'sch_demo';
      
      const [allStudents, allSubjects, schoolData, users, academicYears, classesList, schemeData] = await Promise.all([
        db.getStudents(schoolId),
        db.getSubjects(schoolId),
        db.getSchool(schoolId),
        db.getUsers(schoolId, 'school_admin'),
        db.getAcademicYears(),
        db.getClasses(schoolId),
        db.getGradingScheme(schoolId)
      ]);
      setSubjects(allSubjects);
      setSchool(schoolData);
      setGradingScheme(schemeData);
      
      const normalizedStudents = allStudents.map(s => {
        const cls = classesList.find(c => c.id === s.class_grade || c.name === s.class_grade);
        return {
          ...s,
          class_grade: cls ? cls.name : s.class_grade
        };
      });
      
      let activeHeadTeacher: User | null = null;
      if (urlHeadTeacherName) {
          activeHeadTeacher = { full_name: urlHeadTeacherName } as User;
      } else if (users.length > 0) {
          activeHeadTeacher = users.find(u => 
              u.job_title?.toLowerCase().includes('head') || 
              u.job_title?.toLowerCase().includes('principal') || 
              u.full_name?.toLowerCase().includes('head')
          ) || users[0];
      }
      
      if (!activeHeadTeacher) {
          const allSchoolUsers = await db.getUsers(schoolId);
          if (allSchoolUsers.length > 0) {
              activeHeadTeacher = allSchoolUsers.find(u => u.id === schoolData?.user_id) ||
                                  allSchoolUsers.find(u => u.role === 'school_admin' || u.role === 'co_admin') ||
                                  allSchoolUsers[0];
          }
      }
      
      if (!activeHeadTeacher && user) {
          activeHeadTeacher = user;
      }
      
      setHeadTeacher(activeHeadTeacher);
      
      if (academicYearId) {
          const year = academicYears.find(y => y.id === academicYearId);
          if (year) setAcademicYearName(year.year_name);
      } else {
          const currentYear = academicYears.find(y => y.is_current && y.school_id === schoolId);
          if (currentYear) setAcademicYearName(currentYear.year_name);
      }
      
      let filteredStudents: Student[] = [];
      let cohortStudents: Student[] = [];
 
      if (sIds) {
        const ids = sIds.split(',');
        filteredStudents = normalizedStudents.filter(s => ids.includes(s.id));
        if (filteredStudents.length > 0) {
          const classGrades = Array.from(new Set(filteredStudents.map(s => s.class_grade)));
          const streams = Array.from(new Set(filteredStudents.map(s => s.stream)));
          cohortStudents = normalizedStudents.filter(s => classGrades.includes(s.class_grade) && (!s.stream || streams.includes(s.stream)));
        }
      } else if (sId) {
        const student = normalizedStudents.find(s => s.id === sId);
        if (student) {
            filteredStudents = [student];
            cohortStudents = normalizedStudents.filter(s => 
                s.class_grade === student.class_grade && 
                (!student.stream || s.stream === student.stream)
            );
        }
      } else if (classGrade || stream) {
        filteredStudents = normalizedStudents.filter(s => {
          const matchClass = !classGrade || classGrade === 'All' || s.class_grade === classGrade;
          const matchStream = !stream || stream === 'All' || s.stream === stream;
          return matchClass && matchStream;
        });
        cohortStudents = filteredStudents;
      }

      let activeAcademicYearId = academicYearId;
      if (!activeAcademicYearId) {
          const currentYear = academicYears.find(y => y.is_current && y.school_id === schoolId);
          if (currentYear) activeAcademicYearId = currentYear.id;
      }

      const recsMap: Record<string, ExamRecord[]> = {};
      const cohortStudentIds = cohortStudents.map(s => s.id);
      let allRecs: ExamRecord[] = [];
      
      if (cohortStudentIds.length > 0) {
          allRecs = await db.getExamRecordsByStudentIds(schoolId, cohortStudentIds, activeAcademicYearId || undefined);
      }
      
      cohortStudents.forEach(s => {
          recsMap[s.id] = allRecs.filter(r => r.student_id === s.id);
      });

      const newRankings: Record<string, Record<string, { position: number, total: number }>> = {};
      const newSubjectRankings: Record<string, Record<string, { position: number, total: number }>> = {};
      const groups: Record<string, Student[]> = {};
      cohortStudents.forEach(s => {
          const key = `${s.class_grade}_${s.stream || ''}`;
          if (!groups[key]) groups[key] = [];
          groups[key].push(s);
      });

      const termsToRank = ['Term 1', 'Term 2', 'Term 3'];

      Object.values(groups).forEach(group => {
          // Overall Rankings for all terms
          termsToRank.forEach(t => {
            const scores = group.map(s => {
                const sRecs = recsMap[s.id] || [];
                const avgScore = getStudentAverageForTerm(s.id, t, sRecs, s.class_grade, allSubjects);
                return { id: s.id, score: avgScore };
            });

            const rankedScores = scores.filter(item => item.score >= 0);
            rankedScores.sort((a, b) => b.score - a.score);

            let currentRank = 1;
            rankedScores.forEach((item, index) => {
                if (index > 0 && item.score < rankedScores[index - 1].score) {
                    currentRank = index + 1;
                }
                if (!newRankings[item.id]) newRankings[item.id] = {};
                newRankings[item.id][t] = { position: currentRank, total: rankedScores.length };
            });
          });

          // Subject Rankings
          allSubjects.forEach(sub => {
            const subScores = group.map(s => {
              const sRecs = recsMap[s.id] || [];
              const record = sRecs.find(r => (r.subject_id === sub.id || r.subject_name === sub.name) && r.term === term);
              const hasScore = record && record.score !== undefined && record.score !== null && record.score !== '';
              return { id: s.id, score: hasScore ? Number(record.score) : -1 };
            });

            const rankedSubScores = subScores.filter(item => item.score >= 0);
            rankedSubScores.sort((a, b) => b.score - a.score);

            let currentSubRank = 1;
            rankedSubScores.forEach((item, index) => {
              if (index > 0 && item.score < rankedSubScores[index - 1].score) {
                currentSubRank = index + 1;
              }
              if (!newSubjectRankings[item.id]) newSubjectRankings[item.id] = {};
              newSubjectRankings[item.id][sub.name] = { position: currentSubRank, total: rankedSubScores.length };
              if (sub.id) newSubjectRankings[item.id][sub.id] = { position: currentSubRank, total: rankedSubScores.length };
            });
          });
      });

      setRecordsMap(recsMap);
      setStudents(filteredStudents);
      setRankings(newRankings);
      setSubjectRankings(newSubjectRankings);
      setLoading(false);

      if (print && filteredStudents.length > 0) {
        setTimeout(() => {
          window.print();
        }, 1500);
      }
    };
    init();
  }, []);

  const handleDownloadPDF = async () => {
    if (students.length === 0) return;
    setDownloading(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      for (let i = 0; i < students.length; i++) {
        const student = students[i];
        const element = reportRefs.current[student.id];
        
        if (element) {
          const canvasPromise = html2canvas(element, {
            scale: 1.5, // Good balance of quality and speed
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
            imageTimeout: 15000
          });
          
          const timeoutPromise = new Promise<HTMLCanvasElement>((_, reject) => 
            setTimeout(() => reject(new Error('PDF generation timed out for a student. Please try again or use the Print button.')), 20000)
          );
          
          const canvas = await Promise.race([canvasPromise, timeoutPromise]);
          
          const imgData = canvas.toDataURL('image/jpeg', 0.95);
          const imgProps = pdf.getImageProperties(imgData);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
          
          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
          
          setDownloadProgress(Math.round(((i + 1) / students.length) * 100));
        }
      }
      
      const fileName = students.length === 1 
        ? `Report_${students[0].full_name.replace(/\s+/g, '_')}.pdf`
        : `Reports_${selectedTerm.replace(/\s+/g, '_')}.pdf`;
        
      pdf.save(fileName);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF. Please try using the Print button instead.');
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
      <Loader2 size={40} className="animate-spin text-brand-600" />
      <p className="text-slate-600 font-black uppercase tracking-widest text-xs">Preparing Official Report Cards...</p>
    </div>
  );

  if (students.length === 0) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
      <FileText size={48} className="text-slate-300" />
      <p className="text-slate-600 font-medium">No students found for the selected criteria.</p>
      <Button onClick={() => window.close()} variant="outline">Close Window</Button>
    </div>
  );

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; margin: 0; padding: 0; }
          @page { size: A4 portrait; margin: 0; }
          .page-break { page-break-after: always; }
          *, *::before, *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      <div className="no-print fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-50 p-4 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-brand-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-brand-100">
            <Printer size={24} />
          </div>
          <div>
            <h1 className="text-xl font-black text-gray-900 tracking-tight">Report Card Preview</h1>
            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">{students.length} {students.length === 1 ? 'Official Report' : 'Official Reports'} Ready</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => window.close()} className="border-gray-200 text-gray-600 hover:bg-gray-50 font-bold px-6 h-12 rounded-xl">
            Close
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="border-brand-200 text-brand-700 hover:bg-brand-50 font-bold px-6 h-12 rounded-xl">
            <Printer size={18} className="mr-2" />
            Print
          </Button>
          <Button onClick={handleDownloadPDF} disabled={downloading} className="bg-brand-600 hover:bg-brand-700 text-white shadow-xl shadow-brand-100 px-8 h-12 rounded-xl font-bold">
            {downloading ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Download size={18} className="mr-2" />}
            {downloading ? `Generating PDF... ${downloadProgress}%` : 'Download PDF'}
          </Button>
        </div>
      </div>

      <div style={{ 
        background: "#f1f5f9", 
        minHeight: "100vh", 
        padding: "100px 20px 60px",
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center",
        gap: 60
      }} className="print:p-0 print:bg-white print:gap-0">
        {students.map((s) => (
          <div key={s.id} className="page-break shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] print:shadow-none transition-transform hover:scale-[1.01] duration-500">
            <SingleReportCard 
              reportRef={(el) => { reportRefs.current[s.id] = el; }}
              student={s}
              className={`${s.class_grade} ${s.stream || ''}`.trim()} 
              records={recordsMap[s.id] || []}
              subjects={subjects}
              term={selectedTerm}
              school={school}
              headTeacher={headTeacher}
              rank={rankings[s.id]?.[selectedTerm]}
              allTermRankings={rankings[s.id]}
              subjectRankings={subjectRankings[s.id]}
              config={config}
              academicYearName={academicYearName}
              gradingScheme={gradingScheme}
            />
          </div>
        ))}
      </div>
    </>
  );
};
